import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send, Loader2, Facebook, Instagram, ArrowDownRight } from "lucide-react";
import { Lightning } from "../components/Lightning";
import { FadeUp, MaskedLine } from "../components/Reveal";
import { PHONE, PHONE_HREF, EMAIL, SOCIALS } from "../site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
  </svg>
);

const SOCIAL_ICONS = { tiktok: TikTokIcon, instagram: Instagram, facebook: Facebook };

const SERVICE_OPTIONS = [
  "Diesel Repair & Performance",
  "Suspension Lift & Leveling",
  "Underglow & Wheel Lights",
  "Wheels & Tires",
  "Paint Match / Body Work",
  "Audio & Subs",
  "Full Custom Build",
  "Other",
];

const inputCls =
  "w-full border-b-2 border-zinc-700 bg-transparent px-0 py-4 text-base text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-volt rounded-none";

const INITIAL = { name: "", email: "", phone: "", vehicle: "", service: SERVICE_OPTIONS[0], message: "" };

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
      {label}
    </label>
    {children}
  </div>
);

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent. We'll be in touch soon.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="lg:h-screen lg:overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:h-full">
        <section className="relative flex flex-col justify-between overflow-hidden border-b lg:border-b-0 lg:border-r border-zinc-800 px-6 sm:px-10 pt-32 lg:pt-40 pb-10">
          <Lightning className="pointer-events-none absolute inset-0 h-full w-full opacity-70" interval={[2600, 6000]} />
          <div className="relative">
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-volt mb-4 flex items-center gap-2">
              Get a quote <ArrowDownRight size={14} className="text-bolt-bright" />
            </p>
            <h1 className="font-display font-black uppercase italic tracking-tighter leading-[0.85]" data-testid="contact-headline">
              <MaskedLine delay={0.15} className="text-6xl sm:text-8xl text-white">
                Start
              </MaskedLine>
              <MaskedLine delay={0.3} className="text-6xl sm:text-8xl text-volt glow-volt">
                Your
              </MaskedLine>
              <MaskedLine delay={0.45} className="text-6xl sm:text-8xl text-outline-bolt">
                Build
              </MaskedLine>
            </h1>
          </div>

          <FadeUp delay={0.6} className="relative mt-12">
            <div className="flex flex-col gap-6">
              <a href={PHONE_HREF} data-testid="contact-phone-card" className="group w-fit">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-1">Call or text</p>
                <p className="flex items-center gap-3 font-display text-3xl sm:text-4xl font-bold uppercase text-white transition-colors group-hover:text-volt">
                  <Phone size={22} className="text-volt" /> {PHONE}
                </p>
              </a>
              <a href={`mailto:${EMAIL}`} data-testid="contact-email-card" className="group w-fit">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-1">Email</p>
                <p className="flex items-center gap-3 font-display text-xl sm:text-2xl font-bold uppercase text-white transition-colors group-hover:text-bolt-bright break-all">
                  <Mail size={20} className="shrink-0 text-bolt-bright" /> {EMAIL}
                </p>
              </a>
              <div data-testid="contact-socials-card">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-3">Follow the builds</p>
                <div className="flex gap-3">
                  {SOCIALS.map((s) => {
                    const Icon = SOCIAL_ICONS[s.id];
                    return (
                      <a
                        key={s.id}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        data-testid={`contact-${s.id}-link`}
                        className="flex h-11 w-11 items-center justify-center clip-corner border border-zinc-700 text-zinc-300 transition-colors hover:border-volt hover:text-volt"
                      >
                        <Icon size={17} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </FadeUp>
        </section>

        <section className="flex items-start bg-[#070709] px-6 sm:px-10 lg:px-16 pt-14 lg:pt-36 pb-16 lg:overflow-y-auto">
          <FadeUp delay={0.25} className="w-full">
            <p className="mb-8 max-w-md text-sm leading-relaxed text-zinc-400">
              Tell us about your truck and what you want done. Your request lands straight in our inbox — we usually reply the same day.
            </p>
            <form onSubmit={submit} data-testid="quote-form" className="flex w-full max-w-xl flex-col gap-7">
              <Field label="Name *" htmlFor="quote-name">
                <input id="quote-name" data-testid="quote-form-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <Field label="Phone *" htmlFor="quote-phone">
                  <input id="quote-phone" data-testid="quote-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="865-555-0123" className={inputCls} />
                </Field>
                <Field label="Email *" htmlFor="quote-email">
                  <input id="quote-email" data-testid="quote-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                </Field>
              </div>
              <Field label="Vehicle" htmlFor="quote-vehicle">
                <input id="quote-vehicle" data-testid="quote-form-vehicle-input" value={form.vehicle} onChange={set("vehicle")} placeholder="2020 F-250 Powerstroke" className={inputCls} />
              </Field>
              <Field label="Service *" htmlFor="quote-service">
                <select id="quote-service" data-testid="quote-form-service-select" required value={form.service} onChange={set("service")} className={`${inputCls} cursor-pointer`}>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-carbon">{o}</option>
                  ))}
                </select>
              </Field>
              <Field label="Build details" htmlFor="quote-message">
                <textarea
                  id="quote-message"
                  data-testid="quote-form-message-textarea"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Lift size, wheel specs, lighting colors, interior ideas..."
                  className={`${inputCls} resize-none`}
                />
              </Field>
              <div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-form-submit-button"
                  className="clip-badge inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-volt px-10 py-4 font-display text-xl font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:bg-volt-soft disabled:opacity-60"
                >
                  {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
                  {sending ? "Sending..." : "Send quote request"}
                </button>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                  Goes straight to the shop inbox · No spam, ever
                </p>
              </div>
            </form>
          </FadeUp>
        </section>
      </div>
    </main>
  );
}
